import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  EmptyState,
  Skeleton,
  SkeletonCard,
  failedEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import { affixesOf, formLinkAffixes, hostOf } from "../../../lib/site/config";
import { nameError, normaliseLinkName } from "../formLinkName";
import { useFormLink } from "../hooks/useFormLink";
import { usePlansCreated } from "../hooks/usePlansCreated";
import { useServicesCreated } from "../hooks/useServicesCreated";
import { useSlugAvailability } from "../hooks/useSlugAvailability";
import type { BrandDetails, FormPreviewId } from "../types/brand.types";
import FormLinkCard from "./FormLinkCard";
import FormPhonePreview from "./FormPhonePreview";
import FormSetupRequiredCard from "./FormSetupRequiredCard";
import PreConsultationCard from "./PreConsultationCard";

/**
 * The Form tab: one link, and a picture of what it opens.
 *
 * Two forms are reached through it and only one address is handed out, which
 * is why the switch at the top of this tab changes what is said about the form
 * rather than handing over a second link. An enquiry form is open to anybody
 * holding the address, so that side is the box and the Copy button; a
 * pre-consultation form opens for one client, from a single-use link sent from
 * their own profile, so that side is directions to where that link is sent
 * from — `PreConsultationCard` — and there is no second link to copy here.
 */

const PREVIEWS: Array<{ id: FormPreviewId; label: string }> = [
  { id: "enquiry", label: "Enquiry" },
  { id: "pre-consultation", label: "Pre consultation" },
];

/**
 * What the preview's address bar reads before a name has been chosen. Braced
 * like the box on the card, so the two stand-ins read as the same one waiting
 * on the same word rather than as an address somebody already owns.
 */
const UNCLAIMED = "{yourname}";

/** How long the copy button holds its tick before going back to the link. */
const COPIED_MS = 2000;

interface BrandFormTabProps {
  /**
   * The brand as the Details tab has it — for the logo in the preview, which
   * is the practitioner's own and not ours. Whatever is in the form rather
   * than what was last saved, so a logo just uploaded shows in the phone
   * without a save in between.
   */
  details: BrandDetails;
  /**
   * The way to the two tabs this one can turn out to be waiting on — see
   * `FormSetupRequiredCard`. Handed down rather than navigated to from here,
   * because which tab is open is the page's business and it keeps it in the
   * URL.
   */
  onCreatePlan: () => void;
  onCreateService: () => void;
}

const BrandFormTab: React.FC<BrandFormTabProps> = ({
  details,
  onCreatePlan,
  onCreateService,
}) => {
  const toast = useToast();
  const { link, status, reload, saving, claim, registerDomain } = useFormLink();

  // Whether there is anything to sell, which is what decides whether this tab
  // hands an address out at all - see `FormSetupRequiredCard`. Two bits, and
  // both already cached: the roster and the dashboard ask the same two
  // questions under the same keys.
  const { plansCreated, status: plansStatus } = usePlansCreated();
  const { servicesCreated, status: servicesStatus } = useServicesCreated();

  // One answer for the whole tab. The card, the switch and the phone are three
  // things that arrive at three different moments if each is allowed to decide
  // for itself, and the screen reads as arriving three times. The two setup
  // reads are in it for a fourth reason: they decide which card is drawn, and
  // a link card that appeared and was then replaced by the locked one would be
  // an address shown to somebody who cannot have it yet.
  const waiting =
    status === "loading" ||
    plansStatus === "loading" ||
    servicesStatus === "loading";
  const phase = useLoadPhase(waiting);

  /**
   * Whether the tab is short of something a client's form cannot be finished
   * without.
   *
   * Only where both reads answered and one of them answered no. A read still
   * out is already covered by `waiting`; a read that failed leaves the link
   * where it is, because not knowing is not the same as knowing there is
   * nothing there - the same caution `useSetupGate` takes over the same two
   * bits, and the server is the only thing that actually decides.
   */
  const missingSetup =
    (plansStatus === "ready" && !plansCreated) ||
    (servicesStatus === "ready" && !servicesCreated);

  const [preview, setPreview] = useState<FormPreviewId>("enquiry");
  const [name, setName] = useState("");

  // Only after the field has been left, or a copy attempted: marking a name up
  // as too short while it is being typed is telling somebody off for not
  // having finished.
  const [touched, setTouched] = useState(false);

  // The server's objection to a claim - that the slug belongs to somebody
  // else. Kept apart from the rules this app can check for itself, because it
  // is only true of the exact string that was sent: the next keystroke
  // withdraws it.
  const [rejected, setRejected] = useState<string>();

  const [copied, setCopied] = useState(false);

  // True while the subdomain half of a Save is with the server. The claim has
  // `saving` of its own; the button is busy for both, because it is one press.
  const [registering, setRegistering] = useState(false);

  // Set when a Save claimed the name but could not register the subdomain
  // under it. It is what keeps the box open on a name that is already stored:
  // half of what Save does is still to do, and locking it would leave the one
  // button that can finish the job behind a faded-out Copy.
  const [needsDomain, setNeedsDomain] = useState(false);

  // Seeded during the render that first sees the link rather than in an effect
  // after it - an effect paints an empty box and fills it a frame later, which
  // reads as the screen overwriting itself. Once per arrival: `status` turns
  // `ready` once, and a reload that answers again re-seeds a box the person
  // has not touched.
  const [seeded, setSeeded] = useState(false);
  if (!seeded && status === "ready") {
    setSeeded(true);
    if (link) setName(link.slug);
  }

  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const problem = nameError(name);

  /**
   * Whether the slug is worth asking the server about.
   *
   * Not while it breaks a rule this app already knows - the answer would be
   * about a slug that could never be claimed anyway. And not for the one
   * already stored: it is taken, by the person looking at it, and reporting
   * that as a clash would be absurd.
   */
  const checkable = !problem && name !== (link?.slug ?? "");
  const check = useSlugAvailability(name, checkable);

  // The answer to the exact string in the box beats a rule about its shape:
  // "already used" is the one thing the person cannot work out by reading the
  // field. A rule is only reported once they have finished typing.
  const error =
    rejected ??
    (check === "taken" ? `${name} is already used!` : undefined) ??
    (touched ? problem : undefined);

  /**
   * The same answer the other way up, and only from the server: a name is
   * reported free because the backend said so about that exact string, never
   * because this app could find nothing wrong with it.
   *
   * Not said about the slug already stored. `checkable` withholds it from the
   * one name the person has, so `check` stays `idle` there and telling them
   * their own link is available would be a strange thing to read.
   */
  const available =
    !error && check === "free" ? `${name} is available!` : undefined;

  /**
   * Whether Save has anything to do and nothing standing in the way of it.
   *
   * It opens with the line under the field: the button is pressable exactly
   * when the server has said the name is free, and the sentence saying so is
   * the reason it is. A name half typed has not been asked about yet, and a
   * button offering to claim it would be offering to claim a prefix.
   *
   * Two things besides a `free` open it. A check the server could not answer -
   * `failed`, see `useSlugAvailability` - because that question is a courtesy
   * and the claim is what actually decides; a server too ill to answer it must
   * not be able to hold a name nobody else has. And the name already stored,
   * which is never put to the check at all: that is where a Save pressed again
   * after a failed registration lands.
   */
  const canSave =
    !error &&
    !!name.trim() &&
    (check === "free" ||
      check === "failed" ||
      (!!link && name === link.slug));

  /**
   * The fixed halves of the address the box draws.
   *
   * From the stored link where there is one, because `share_url` is the
   * backend's own answer about where it serves - which on a developer machine
   * is nothing the template here would produce. The template stands in only
   * before anything has been claimed, when there is no better answer to have.
   */
  const { prefix, suffix } =
    (link && affixesOf(link.shareUrl, link.slug)) ?? formLinkAffixes();

  /**
   * Whether the box is showing a finished link rather than a name being
   * chosen.
   *
   * Read off the record rather than remembered from the press, so a link
   * claimed in some earlier session opens locked: the box is asking whether
   * there is anything left to decide, and for a name already stored there is
   * not. A name typed over that one unlocks it again by the same rule - it is
   * no longer the stored one - though nothing on this card offers to.
   */
  const locked = !!link && name === link.slug && !needsDomain;

  const handleNameChange = (value: string) => {
    setName(normaliseLinkName(value));
    // The rejection was about the string that was sent, and this is no longer
    // that string.
    setRejected(undefined);
    setCopied(false);
  };

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), COPIED_MS);
      toast.success("Link copied", url);
    } catch (failure) {
      console.error("could not copy the form link:", failure);
      // The link itself in the message, so the press was still worth something
      // - it can be read off the screen and typed.
      toast.error("Could not copy the link", `Your link is ${url}`);
    }
  };

  /** The subdomain half of a Save, with the button held busy for it. */
  const register = async (slug: string): Promise<boolean> => {
    setRegistering(true);
    try {
      return await registerDomain(slug);
    } finally {
      setRegistering(false);
    }
  };

  /**
   * The whole of the commitment: the slug claimed, then the subdomain
   * registered under it.
   *
   * Both, and in that order, because they are two records of one name and the
   * second cannot be written before the first has settled what the name is.
   * The box locks only when both are stored - a link whose subdomain is
   * missing is one whose client forms cannot be built, and offering to copy it
   * would be offering half a thing.
   */
  const handleSave = async () => {
    setTouched(true);
    if (problem || check === "taken") return;

    // Already stored under this exact name, which is where a Save pressed
    // again after a failed registration lands: there is nothing to claim, and
    // the missing record is the only thing left to write.
    if (link && name === link.slug) {
      setNeedsDomain(!(await register(link.slug)));
      return;
    }

    const result = await claim(name);
    if (result.outcome === "rejected") {
      setRejected(result.message);
      return;
    }
    // `failed` has already been said in a toast by the hook.
    if (result.outcome === "failed") return;

    // The slug as the backend stored it, and the address it built - it is the
    // stored one that will actually answer. The two services have to name the
    // same host, so what is registered is the stored slug and never what was
    // typed.
    setName(result.link.slug);
    const registered = await register(result.link.slug);
    setNeedsDomain(!registered);

    // One sentence for one press, and only once both records are in. A failed
    // registration has already said its own piece, in the terms of the thing
    // that will not work.
    if (registered) toast.success("Your link is saved", result.link.shareUrl);
  };

  /**
   * The claimed address onto the clipboard, and nothing else.
   *
   * Reachable only from the locked box, so there is always a stored link
   * behind it — and nothing to store, which is the point: every call this
   * screen makes belongs to Save. A press here reaches the clipboard and not
   * the server, so it cannot fail, cannot be slow, and cannot change what is
   * saved under a name somebody is only sharing.
   */
  const handleCopy = async () => {
    if (!link) return;
    await copy(link.shareUrl);
  };

  return (
    // From `lg` up the tab owns what is left of the viewport rather than
    // growing past it, so the phone beside the card can be sized to fit it.
    <div className="flex flex-col lg:h-full lg:min-h-0">
      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3">
        <h2 className="text-[15px] 3xl:text-[16px] font-medium text-ink">
          Share your forms with clients
        </h2>

        {/* The same pill group the Details tab switches its documents with,
            and the Billings tab its two screens: one rounded track holding
            both halves, with only the selected one tinted.

            Its own block stands here while the read is out, the way the
            document switcher on the Details tab waits: it flips between two
            previews, and neither is on screen yet. */}
        {waiting ? (
          phase === "slow" && <Skeleton className="h-9 w-[228px] rounded-full" />
        ) : (
        <div
          role="group"
          aria-label="Preview form"
          className="inline-flex items-center gap-1 rounded-full border border-line bg-white p-1"
        >
          {PREVIEWS.map((item) => {
            const selected = item.id === preview;
            return (
              <button
                key={item.id}
                type="button"
                aria-pressed={selected}
                onClick={() => setPreview(item.id)}
                className={`rounded-full px-3.5 py-1.5 text-[13px] 3xl:text-[14px] transition-colors ${
                  selected
                    ? "bg-[#BFF4FB] font-medium text-[#007E8C]"
                    : "text-ink-muted hover:text-ink"
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>
        )}
      </div>

      {/* The link is the screen, so it takes the room; the phone is a fixed
          column beside it and drops under it before the card gets narrow
          enough to wrap the address. */}
      {/* `grid-rows-[minmax(0,1fr)]` from `lg` up, and it is what makes the
          phone fit: a row left to `auto` is sized by what is in it, so the
          card's `h-full` would resolve against its own contents and the
          preview would grow the page instead of being scaled into it.

          `mt-5` and not `mt-4`, to match the gap the page leaves above the
          header row: the switcher is the tallest thing in that row, so it is
          the switcher those two figures are measured from and it sits with
          equal air on either side. */}
      <div className="mt-5 grid grid-cols-1 gap-5 lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_minmax(300px,400px)] lg:grid-rows-[minmax(0,1fr)] 3xl:gap-6">
        {/* The card is a fixed height and the phone is not, so it is this
            column that gives way on a short window - the same split the
            Details tab makes, where the form scrolls and the document
            beside it stays whole. */}
        <div className="lg:min-h-0 lg:overflow-y-auto">
          {waiting ? (
            // One block for the card, not for the field and the three lines
            // that will be in it.
            <SkeletonCard phase={phase} className="h-[360px]" />
          ) : missingSetup ? (
            // Ahead of both cards and of the failed read alike: what is missing
            // is missing on either side of the switch, and an address that
            // could not be read back is beside the point while there is none to
            // be had yet.
            <FormSetupRequiredCard
              onCreatePlan={onCreatePlan}
              onCreateService={onCreateService}
            />
          ) : preview === "pre-consultation" ? (
            // Nothing on this card comes from the server, and it stands even
            // where the link could not be read: the pre-consultation form is
            // sent from a client's own profile, so what it owes the
            // practitioner is directions, which are true either way.
            <PreConsultationCard />
          ) : status === "failed" ? (
            <div className="flex min-h-[240px] flex-col items-center justify-center rounded-md border border-line bg-white">
              <EmptyState
                {...failedEmptyState(
                  "We could not load your link",
                  <Button size="sm" variant="secondary" onClick={reload}>
                    Try again
                  </Button>
                )}
                description="Nothing has been lost - this is only what is saved being read back."
              />
            </div>
          ) : (
            <FormLinkCard
              name={name}
              onNameChange={handleNameChange}
              onBlur={() => setTouched(true)}
              prefix={prefix}
              suffix={suffix}
              error={error}
              available={available}
              url={link?.shareUrl ?? ""}
              locked={locked}
              saving={saving || registering}
              copied={copied}
              canSave={canSave}
              onSave={() => {
                void handleSave();
              }}
              onCopy={() => {
                void handleCopy();
              }}
              onCopyBlocked={() =>
                toast.info(
                  "Save your link first",
                  "Press Save, and the whole address is yours to copy from the button beside the box."
                )
              }
            />
          )}
        </div>

        {/* A flex column, so the phone inside can be a `flex-1 min-h-0` item
            rather than a `h-full` one. The difference matters: `h-full` on a
            flex item asks the container how tall it is, and a container sized
            by its own contents answers with the phone - which is how a phone
            too big for the card stays too big for the card. */}
        {waiting ? (
          // The phone could be drawn the instant this mounts - it is a picture
          // of a form, and nothing in it comes from the server. It waits all
          // the same, because a column that arrives ahead of the one beside it
          // reads as the screen arriving twice, which is exactly what it did.
          // Same block, same phase, so the pair land together.
          <SkeletonCard
            phase={phase}
            className="min-h-[560px] lg:min-h-0 lg:h-full"
          />
        ) : (
        <div className="flex flex-col overflow-hidden rounded-md border border-line bg-white p-5 lg:h-full lg:min-h-0 3xl:p-6">
          <FormPhonePreview
            details={details}
            preview={preview}
            // The address as it stands, so typing a name changes what the
            // phone shows — which is the whole reason the two are side by
            // side. It is a picture of an address, not a link to one.
            host={hostOf(`${prefix}${name.trim() || UNCLAIMED}${suffix}`)}
          />
        </div>
        )}
      </div>
    </div>
  );
};

export default BrandFormTab;
